import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { API_URL } from "../config";
import { useApp } from "../context/AppContext";
import "./PromptIn.css";

function getToken() {
  return localStorage.getItem("token") || "";
}

export default function PromptIn() {
  const navigate = useNavigate();
  const {
    idea, setIdea,
    chatHistory, setChatHistory,
    promptEng, setPromptEng,
    setPromptEsp,
    setMdvObject,
    setHashtagId,
    setAutoGenerate,
    setResumen,
  } = useApp();

  const [input, setInput]           = useState("");
  const [ideaLocked, setIdeaLocked] = useState(chatHistory.length > 0);
  const [loading, setLoading]       = useState(false);
  const [generating, setGenerating] = useState(false);

  const chatEndRef = useRef(null);
  const inputRef   = useRef(null);

  // Scroll chat to bottom on new messages
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatHistory]);

  // Llamada directa al backend LLM (sin polling)
  const callLlm = useCallback(async (endpoint, body) => {
    const res = await fetch(`${API_URL}${endpoint}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `HTTP ${res.status}`);
    }
    return await res.json();
  }, []);

  // Send idea to start brief
  const startBrief = useCallback(async () => {
    if (!idea.trim() || loading) return;
    setIdeaLocked(true);
    setLoading(true);

    const userMsg = { role: "user", content: idea.trim() };
    const nextHistory = [...chatHistory, userMsg];
    setChatHistory(nextHistory);

    try {
      const data = await callLlm("/assistant-chat", { text: idea.trim(), history: chatHistory });
      setChatHistory([...nextHistory, { role: "assistant", content: data.reply, tokens: data.tokens }]);
    } catch (e) {
      setChatHistory([...nextHistory, {
        role: "assistant",
        content: `Error al conectar con el asistente: ${e.message}`,
      }]);
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [idea, loading, chatHistory, setChatHistory, callLlm]);

  // Send chat message
  const sendMessage = useCallback(async () => {
    if (!input.trim() || loading) return;
    const text = input.trim();
    setInput("");
    setLoading(true);

    const userMsg = { role: "user", content: text };
    const nextHistory = [...chatHistory, userMsg];
    setChatHistory(nextHistory);

    try {
      const data = await callLlm("/assistant-chat", { text, history: chatHistory });
      setChatHistory([...nextHistory, { role: "assistant", content: data.reply, tokens: data.tokens }]);
    } catch (e) {
      setChatHistory([...nextHistory, {
        role: "assistant",
        content: `Error de conexión: ${e.message}`,
      }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, chatHistory, setChatHistory, callLlm]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!ideaLocked) {
        startBrief();
      } else {
        sendMessage();
      }
    }
  };

  // Expand prompt (reutilizable por los 3 action tags + botón)
  const expandPrompt = useCallback(async () => {
    const data = await callLlm("/expand-prompt", { text: idea, chat_history: chatHistory });
    const eng = data.prompt_eng || data.prompt || "";
    const esp = data.prompt_esp || "";
    setPromptEng(eng);
    setPromptEsp(esp);
    setMdvObject(data.mdv || null);
    setHashtagId(data.hashtag_id || "");
    setResumen(data.resumen || "");
    return { eng, esp };
  }, [idea, chatHistory, callLlm, setPromptEng, setPromptEsp, setMdvObject, setHashtagId, setResumen]);

  // Generate Prompt OUT (botón principal)
  const generatePrompt = useCallback(async () => {
    if (!idea.trim() || generating) return;
    setGenerating(true);
    try {
      await expandPrompt();
      navigate("/keyframes/prompter");
    } catch (e) {
      setChatHistory([...chatHistory, {
        role: "assistant",
        content: `Error al generar el prompt: ${e.message}`,
      }]);
    } finally {
      setGenerating(false);
    }
  }, [idea, chatHistory, generating, setChatHistory, expandPrompt, navigate]);


  // Action: Genera Prompt — expand y muestra en chat
  const actionGeneraPrompt = useCallback(async () => {
    if (!idea.trim() || generating) return;
    setGenerating(true);
    try {
      const { eng, esp } = await expandPrompt();
      setChatHistory(prev => [...prev, {
        role: "assistant",
        content: `**Prompt generado:**\n\n**ENG:**\n${eng}\n\n**ESP:**\n${esp}\n\n_Pulsa "✦ Generar Prompt OUT" para ir al Prompter._`,
      }]);
    } catch (e) {
      setChatHistory(prev => [...prev, {
        role: "assistant",
        content: `Error al generar el prompt: ${e.message}`,
      }]);
    } finally {
      setGenerating(false);
    }
  }, [idea, generating, expandPrompt, setChatHistory]);

  // Action tag: Ir a Prompter — expand y navega
  const actionIrAPrompter = useCallback(async () => {
    if (!idea.trim() || generating) return;
    setGenerating(true);
    try {
      await expandPrompt();
      navigate("/keyframes/prompter");
    } catch (e) {
      setChatHistory(prev => [...prev, {
        role: "assistant",
        content: `Error al generar el prompt: ${e.message}`,
      }]);
    } finally {
      setGenerating(false);
    }
  }, [idea, generating, expandPrompt, setChatHistory, navigate]);

  // Action tag: Generar Imagen — expand, flag autoGenerate, navega a Generar
  const actionGenerarImagen = useCallback(async () => {
    if (!idea.trim() || generating) return;
    setGenerating(true);
    try {
      await expandPrompt();
      setAutoGenerate(true);
      navigate("/keyframes/prompter");
    } catch (e) {
      setChatHistory(prev => [...prev, {
        role: "assistant",
        content: `Error al generar el prompt: ${e.message}`,
      }]);
    } finally {
      setGenerating(false);
    }
  }, [idea, generating, expandPrompt, setChatHistory, setAutoGenerate, navigate]);

  // Reset
  const resetAll = () => {
    setIdeaLocked(false);
    setIdea("");
    setChatHistory([]);
    setPromptEng("");
    setPromptEsp("");
    setMdvObject(null);
    setHashtagId("");
    setInput("");
  };

  const hasChat = chatHistory.length > 0;

  return (
    <div className="pi-page">

      {/* ── Idea inicial (2 líneas) ── */}
      <div className="pi-idea-bar">
        <div className="pi-idea-label">IDEA INICIAL</div>
        <div className="pi-idea-row">
          <textarea
            className="pi-idea-input"
            rows={2}
            placeholder="Describe tu idea — personaje, escena, atmósfera, estilo..."
            value={idea}
            onChange={e => setIdea(e.target.value)}
            disabled={ideaLocked}
            onKeyDown={e => {
              if (e.key === "Enter" && !e.shiftKey && !ideaLocked) {
                e.preventDefault();
                startBrief();
              }
            }}
          />
          {!ideaLocked ? (
            <button
              className="pi-idea-btn"
              onClick={startBrief}
              disabled={!idea.trim() || loading}
              title="Iniciar brief"
            >↑</button>
          ) : (
            <button
              className="pi-idea-btn pi-idea-btn--reset"
              onClick={resetAll}
              title="Reiniciar"
            >↺</button>
          )}
        </div>
      </div>

      {/* ── Asistente de fotografía (flex:1, scroll) ── */}
      <div className="pi-chat">
        <div className="pi-chat-label">ASISTENTE DE FOTOGRAFÍA</div>
        <div className="pi-chat-scroll">
          {(!Array.isArray(chatHistory) || chatHistory.length === 0) && (
            <div className="pi-chat-empty">
              Escribe tu idea arriba y pulsa ↑ para comenzar con el asistente.
            </div>
          )}
          {(Array.isArray(chatHistory) ? chatHistory : []).map((msg, i) => (
            <div key={i} className={`pi-msg pi-msg--${msg.role}`}>
              {msg.role === "assistant" ? (
                <>
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      del: ({ children }) => (
                        <span
                          className="pi-option-link"
                          onClick={() => {
                            const text = typeof children === 'string' ? children
                              : Array.isArray(children) ? children.join('') : String(children);
                            setInput(prev => prev ? prev + " " + text : text);
                            inputRef.current?.focus();
                          }}
                        >
                          {children}
                        </span>
                      ),
                    }}
                  >{msg.content}</ReactMarkdown>
                </>
              ) : (
                msg.content
              )}
              {msg.tokens && (
                <span className="pi-msg-tokens">
                  ({msg.tokens.input}/{msg.tokens.output} tokens)
                </span>
              )}
            </div>
          ))}
          {loading && (
            <div className="pi-msg pi-msg--assistant pi-msg--typing">
              <span className="typing-dot" />
              <span className="typing-dot" style={{ animationDelay: "0.2s" }} />
              <span className="typing-dot" style={{ animationDelay: "0.4s" }} />
            </div>
          )}
          <div ref={chatEndRef} />
        </div>
      </div>

      {/* ── Decisiones + botones de acción ── */}
      <div className="pi-input-bar">
        <div className="pi-input-row">
          <textarea
            ref={inputRef}
            className="pi-user-input"
            rows={1}
            placeholder={hasChat ? "Decisiones visuales..." : "Inicia el brief primero"}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={!hasChat || loading}
          />
          {hasChat && (
            <>
              <button
                className="pi-action-btn pi-action-btn--refresh"
                onClick={sendMessage}
                disabled={!input.trim() || loading || generating}
              >
                Enviar
              </button>
              <button
                className="pi-action-btn pi-action-btn--prompt"
                onClick={generatePrompt}
                disabled={!idea.trim() || generating}
              >
                {generating ? <><span className="pi-aperture" /> ...</> : "Generar Prompt"}
              </button>
              <button
                className="pi-action-btn pi-action-btn--image"
                onClick={actionGenerarImagen}
                disabled={!idea.trim() || generating}
              >
                Generar Imagen
              </button>
            </>
          )}
        </div>
      </div>

    </div>
  );
}
